import React, { useEffect } from 'react';
import { Form, Input } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditorWithAttachments from '@/components/ui/RichTextEditorWithAttachments';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
} from '@/pages/inside/tenant/style';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/tenant/project/projectTabStyles';

const PHASE_DESCRIPTION_PLACEHOLDER =
    'Add description — bold, lists, @mentions. Attach files or links.';

/**
 * Create / edit project phase — description uses RichTextEditorWithAttachments.
 */
export default function PhaseModal({
    mode,
    open,
    onCancel,
    phaseForm,
    mentionUsers,
    mentionMilestones,
    mentionSprints,
    handlePhaseFilesSelected,
    handlePhaseLinkRequest,
    phasePendingAttachments,
    removePhasePendingAttachment,
    phasePendingLinkAttachments,
    removePhasePendingLinkAttachment,
    selectedPhaseAttachmentSections,
    onRemoveExistingAttachment,
    onSubmit,
    submitPending,
}) {
    useEffect(() => {
        if (!open) return;
        const id = requestAnimationFrame(() => {
            document
                .querySelectorAll('.ant-modal-wrap.modal-size-md.ant-modal-wrap-open .ant-modal-body')
                .forEach((el) => {
                    el.scrollTop = 0;
                });
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    const isEdit = mode === 'edit';
    const title = (
        <ModalChromeHeading>{isEdit ? 'Edit phase' : 'Create phase'}</ModalChromeHeading>
    );

    const titleValue = Form.useWatch('title', phaseForm) || '';

    return (
        <AppModal
            size="md"
            title={title}
            open={open}
            onCancel={onCancel}
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button $primary={false} $size="14px" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={submitPending}
                        icon={<SendOutlined />}
                        onClick={onSubmit}
                    >
                        {isEdit ? 'Save phase' : 'Create phase'}
                    </Button>
                </CreateRoleModalFooter>
            }
        >
            <CreateRoleModalBody $flush $noMaxHeight style={{ paddingBottom: 28 }}>
                <Form form={phaseForm} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                    <Form.Item
                        name="title"
                        label={<span style={submissionRichDescriptionLabelStyle}>Title</span>}
                        rules={[{ required: true, message: 'Enter phase title' }]}
                        style={{ marginBottom: 4 }}
                    >
                        <Input placeholder="e.g. Discovery" size="large" maxLength={40} />
                    </Form.Item>
                    <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--ant-color-text-description, #8c8c8c)', marginBottom: 12 }}>
                        {titleValue.length}/40
                    </div>
                    <Form.Item
                        label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                        style={submissionRichDescriptionFormItemStyle}
                    >
                        <RichTextEditorWithAttachments
                            form={phaseForm}
                            fieldName="description"
                            isEdit={isEdit}
                            open={open}
                            mentionUsers={mentionUsers}
                            mentionMilestones={mentionMilestones}
                            mentionSprints={mentionSprints}
                            placeholder={PHASE_DESCRIPTION_PLACEHOLDER}
                            
                            pendingFiles={phasePendingAttachments}
                            onFilesSelected={handlePhaseFilesSelected}
                            onRemovePendingFile={removePhasePendingAttachment}
                            
                            pendingLinks={phasePendingLinkAttachments}
                            onRemovePendingLink={removePhasePendingLinkAttachment}
                            onLinkRequest={handlePhaseLinkRequest}
                            
                            existingFiles={selectedPhaseAttachmentSections?.files || []}
                            existingLinks={selectedPhaseAttachmentSections?.links || []}
                            onRemoveExistingAttachment={onRemoveExistingAttachment}
                        />
                    </Form.Item>
                </Form>
            </CreateRoleModalBody>
        </AppModal>
    );
}
